import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableHead, TableRow, TableCell, TextField, MenuItem, Button,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Tooltip, Menu,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PaymentsIcon from '@mui/icons-material/Payments';
import PageHelp from '../../components/common/PageHelp';
import LoadMoreBar from '../../components/common/LoadMoreBar';
import { useCursorList, toCursorPage } from '../../hooks/useCursorList';
import { useToast } from '../../lib/useToast';
import { getApiErrorMessage } from '../../lib/toast';
import { useConfirm } from '../../components/feedback/ConfirmDialog';
import {
  createPayoutBatch, listPayoutBatches, getPayoutBatch, markPayoutBatchPaid, cancelPayoutBatch,
  PayoutBatchRow, PayoutBatchItemRow,
} from '../../services/payoutService';
import { exportPayoutBatch, PayoutCsvFormat, PAYOUT_CSV_FORMATS } from '../../services/exportService';

const statusColor = (s: string): 'default' | 'success' | 'warning' | 'error' | 'info' =>
  s === 'paid' ? 'success' : s === 'cancelled' ? 'error' : s === 'draft' ? 'warning' : s === 'processing' ? 'info' : 'default';

const BATCH_STATUSES = ['draft', 'processing', 'paid', 'cancelled'] as const;

/** "CSV ▾" button that lets the operator pick which bank's bulk-upload template to download. */
const CsvMenuButton: React.FC<{ onPick: (format: PayoutCsvFormat) => void }> = ({ onPick }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const pick = (format: PayoutCsvFormat) => {
    setAnchorEl(null);
    onPick(format);
  };
  return (
    <>
      <Button
        size="small" startIcon={<DownloadIcon fontSize="small" />} endIcon={<ArrowDropDownIcon fontSize="small" />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        CSV
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {PAYOUT_CSV_FORMATS.map((f) => (
          <MenuItem key={f.value} onClick={() => pick(f.value)}>{f.label}</MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface Props {
  currentCity?: string;
  homeCity?: string;
  defaultAmount?: number;
  canPay: boolean;
  onCoinsChanged?: () => void;
}

const PayoutBatchesPanel: React.FC<Props> = ({ currentCity, homeCity, defaultAmount, canPay, onCoinsChanged }) => {
  const toast = useToast();
  const confirm = useConfirm();

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Table filters (status + created-at range) — server-side, reset the cursor list on change.
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const hasFilters = statusFilter !== 'all' || !!fromDate || !!toDate;

  const {
    rows: batches, isLoading: loading, hasMore, isLoadingMore, loadMore, refetch,
  } = useCursorList<PayoutBatchRow>({
    key: ['payout-batches', statusFilter, fromDate, toDate],
    pageSize: 10, // matches RideList — 10 rows, then "Load more"
    fetchPage: async ({ cursor, limit }) => {
      const res = await listPayoutBatches({
        cursor,
        limit,
        status: statusFilter === 'all' ? undefined : statusFilter,
        from: fromDate || undefined,
        to: toDate || undefined,
      });
      return toCursorPage<PayoutBatchRow>(res.data);
    },
  });

  // Batch detail dialog
  const [detail, setDetail] = useState<(PayoutBatchRow & { items: PayoutBatchItemRow[] }) | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (defaultAmount && amount === '') setAmount(String(defaultAmount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAmount]);

  // Large batches are debited by the background worker; poll while any batch is still 'processing'
  // so the table flips to 'paid' on its own without a manual refresh.
  useEffect(() => {
    if (!batches.some((b) => b.status === 'processing')) return;
    const t = setInterval(refetch, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches]);

  const handleCreate = async () => {
    const amt = parseInt(amount, 10);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a positive coin amount per driver');
      return;
    }
    const ok = await confirm({
      title: 'Create payout batch?',
      message: `This snapshots every eligible driver (has a UPI + coins > 0) in the current filter and reserves up to ${amt} coins each. Coins are only debited when you later mark the batch paid.`,
      confirmText: 'Create batch',
    });
    if (!ok) return;
    setCreating(true);
    try {
      const res = await createPayoutBatch({
        amount: amt,
        currentCity: currentCity || undefined,
        homeCity: homeCity || undefined,
        note: note.trim() || undefined,
      });
      toast.success(`Batch #${res.data.batch.id} created (${res.data.batch.totalDrivers} drivers, ${res.data.batch.totalCoins} coins)`);
      setNote('');
      await refetch();
      openDetail(res.data.batch.id);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to create batch'));
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const res = await getPayoutBatch(id);
      setDetail(res.data);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load batch'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleMarkPaid = async (id: number) => {
    const ok = await confirm({
      title: 'Mark batch paid?',
      message:
        'Only do this AFTER you have uploaded the CSV to RazorpayX/Cashfree and the transfers went through. This debits each driver\'s coins and notifies them. Rows where the driver no longer has enough coins are skipped. Large batches process in the background — the status will move to “processing”, then “paid”.',
      confirmText: 'Mark paid',
    });
    if (!ok) return;
    setBusyId(id);
    try {
      const res = await markPayoutBatchPaid(id);
      if (res.data.queued) {
        toast.success(`Batch #${id} queued — ${res.data.totalDrivers ?? ''} drivers are being paid in the background.`);
      } else {
        toast.success(`Paid ${res.data.paid}, skipped ${res.data.skipped} · ${res.data.coinsDebited} coins debited`);
      }
      await refetch();
      if (detail?.id === id) await openDetail(id);
      onCoinsChanged?.();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to mark batch paid'));
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (id: number) => {
    const ok = await confirm({
      title: 'Cancel batch?',
      message: 'Discards this draft batch. No coins were debited, so nothing is refunded.',
      confirmText: 'Cancel batch',
      destructive: true,
    });
    if (!ok) return;
    setBusyId(id);
    try {
      await cancelPayoutBatch(id);
      toast.success('Batch cancelled');
      await refetch();
      if (detail?.id === id) setDetail(null);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to cancel batch'));
    } finally {
      setBusyId(null);
    }
  };

  const handleExport = async (batch: Pick<PayoutBatchRow, 'id' | 'status'>, format: PayoutCsvFormat) => {
    // The bank-upload CSV only ever contains UNPAID rows (the double-payment guard) — for a
    // paid/cancelled/processing batch it would be empty, which reads as a bug. Say so instead.
    if (batch.status !== 'draft') {
      toast.info(
        `Batch #${batch.id} is ${batch.status} — the bank-upload CSV lists only unpaid rows, so it would be empty. Use "View" to see every row and its status.`,
      );
      return;
    }
    const label = PAYOUT_CSV_FORMATS.find((f) => f.value === format)?.label ?? format;
    try {
      await exportPayoutBatch(batch.id, format);
      toast.success(`Batch #${batch.id} ${label} downloaded`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to export batch'));
    }
  };

  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <PaymentsIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Reward payouts (coins → cash)</Typography>
        <PageHelp k="driverPayoutsCoins" />
      </Box>

      {/* Create batch */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 1 }}>
        <TextField
          label="Coins per driver"
          size="small" type="number" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          sx={{ width: 180 }}
          helperText="Capped at each driver's balance"
          disabled={!canPay}
        />
        <TextField
          label="Note (optional)" size="small" value={note}
          onChange={(e) => setNote(e.target.value)} sx={{ width: 220 }} helperText=" " disabled={!canPay}
        />
        <Tooltip title={canPay ? '' : 'Requires the wallet:adjust permission'} disableHoverListener={canPay}>
          <span>
            <Button
              variant="contained" sx={{ mt: 0.5 }} onClick={handleCreate}
              disabled={!canPay || creating}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {creating ? 'Creating…' : 'Create batch'}
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Uses the Current/Home city filters below. Flow: Create → download the batch CSV (RazorpayX or
        Cashfree template) → upload to your payout dashboard → <strong>Mark paid</strong> (that's when
        coins are debited &amp; drivers notified).
      </Typography>

      {/* Table filters */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
        <TextField
          select size="small" label="Status" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All</MenuItem>
          {BATCH_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" label="From" type="date" value={fromDate}
          onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
        />
        <TextField
          size="small" label="To" type="date" value={toDate}
          onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
        />
        {hasFilters && (
          <Button size="small" color="inherit" onClick={() => { setStatusFilter('all'); setFromDate(''); setToDate(''); }}>
            Clear
          </Button>
        )}
      </Box>

      {/* Batches table */}
      <Box sx={{ overflowX: 'auto', mt: 1 }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              {['#', 'Status', 'Drivers', 'Coins (₹)', 'Created', ''].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
            ) : batches.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>{hasFilters ? 'No batches match the filters' : 'No payout batches yet'}</TableCell></TableRow>
            ) : (
              batches.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>{b.id}</TableCell>
                  <TableCell><Chip size="small" label={b.status} color={statusColor(b.status)} variant={b.status === 'draft' ? 'outlined' : 'filled'} /></TableCell>
                  <TableCell>
                    {b.status === 'processing' && b.processedDrivers != null
                      ? `${b.processedDrivers} / ${b.totalDrivers}`
                      : b.totalDrivers}
                  </TableCell>
                  <TableCell>₹{b.totalCoins}</TableCell>
                  <TableCell>{fmtDate(b.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <Button size="small" onClick={() => openDetail(b.id)}>View</Button>
                      <CsvMenuButton onPick={(format) => handleExport(b, format)} />
                      {b.status === 'draft' && canPay && (
                        <>
                          <Button size="small" variant="contained" disabled={busyId === b.id} onClick={() => handleMarkPaid(b.id)}>Mark paid</Button>
                          <Button size="small" color="inherit" disabled={busyId === b.id} onClick={() => handleCancel(b.id)}>Cancel</Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      {!loading && batches.length > 0 && (
        <LoadMoreBar count={batches.length} hasMore={hasMore} loading={isLoadingMore} onLoadMore={loadMore} />
      )}

      {/* Batch detail dialog */}
      <Dialog open={!!detail || detailLoading} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Batch #{detail?.id} {detail && <Chip size="small" sx={{ ml: 1 }} label={detail.status} color={statusColor(detail.status)} />}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {detail.totalDrivers} drivers · ₹{detail.totalCoins} total · target ₹{detail.amountPerDriver}/driver
                {detail.note ? ` · ${detail.note}` : ''}
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 620 }}>
                  <TableHead>
                    <TableRow>
                      {['Driver', 'UPI', 'Coins (₹)', 'Status'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.items.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>#{it.driverId}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>{it.upiVpa}</TableCell>
                        <TableCell>₹{it.coins}</TableCell>
                        <TableCell>
                          <Chip size="small" label={it.status} variant="outlined"
                            color={it.status === 'paid' ? 'success' : it.status === 'failed' ? 'error' : it.status === 'skipped' ? 'warning' : 'default'} />
                          {it.failureReason ? <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{it.failureReason}</Typography> : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {detail && <CsvMenuButton onPick={(format) => handleExport(detail, format)} />}
          {detail?.status === 'draft' && canPay && (
            <Button variant="contained" disabled={busyId === detail.id} onClick={() => handleMarkPaid(detail.id)}>Mark paid</Button>
          )}
          <Button color="inherit" onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PayoutBatchesPanel;
